import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid, Line, LineChart, ReferenceArea, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import type { IncidentDetail, IncidentListItem } from "../types";

// «Аварии» — data-only view of the documented 2026-05-25 events (16:07, 17:41):
// factual KPI, alarm log (EN+RU), per-second charts and table. No analysis.

const UNIT_COLOR: Record<string, string> = {
  "UNIT 1": "#34c759", "UNIT 2": "#ff9500", STAT: "#af52de", System: "#5a6d7c",
};
const CAT_COLOR: Record<string, string> = {
  TRIP: "#ff453a", VOLTAGE_MISS: "#ff9500", COMM_LOST: "#a76bff",
  POWER_SUPPLY: "#e3350d", LUBRICATION: "#0aa5c7", OTHER: "#5a6d7c",
  SYSTEM: "#5a6d7c", AUTH: "#5a6d7c",
};
// Highlight colours for the flagged items on the tab.
const DIFF_COLOR = "#bf5af2"; // differential protection (87H)
const Q1_COLOR = "#32ade6";   // H01 breaker Q1
const PRE_COLOR = "#ffd60a";  // pre-fault deviation (before the trip)
const fmtSec = (iso: string) => iso.slice(11, 19);

export default function IncidentsPanel({ initialKey }: { embedded?: boolean; initialKey?: string | null } = {}) {
  const { t } = useT();
  const [list, setList] = useState<IncidentListItem[] | null>(null);
  const [key, setKey] = useState<string>(initialKey ?? "i1607");
  const [data, setData] = useState<IncidentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ incidents: IncidentListItem[] }>("/api/incidents")
      .then((d) => { setList(d.incidents); if (!initialKey && d.incidents.length) setKey(d.incidents[0].key); })
      .catch((e: ApiError) => setError(e.message));
  }, []);

  useEffect(() => {
    setData(null);
    api.get<IncidentDetail>(`/api/incidents/${key}`)
      .then(setData).catch((e: ApiError) => setError(e.message));
  }, [key]);

  const rows = useMemo(() => (data?.points ?? []).map((p) => ({
    label: fmtSec(p.t), full: p.t.slice(0, 19).replace("T", " "),
    rs: p.rs, st: p.st, rt: p.rt, kw: p.kw, kvar: p.kvar, hz: p.hz,
    pos: p.pos, speed: p.speed, vals: p.vals, pre: p.pre,
  })), [data]);

  // Table: drop fully dead rows; seconds where only the turbine is still
  // coasting (overspeed decay, voltage already 0) carry data — keep them.
  const tripHms = data ? fmtSec(data.trip) : "";
  const tableRows = useMemo(() => rows.filter((r) =>
    r.label === tripHms || !!(r.rs || r.st || r.rt || r.kw || (r.speed ?? 0) > 1)),
    [rows, tripHms]);

  // Full metric grid: order the 43 SCADA metrics by group, keep each metric's
  // index into point.vals so every cell can be read positionally.
  const GROUP_ORDER = ["power", "voltage", "current", "temperature", "pressure", "level", "control", "counter"];
  const orderedMetrics = useMemo(() => {
    const withIdx = (data?.metrics ?? []).map((m, i) => ({ ...m, idx: i }));
    const known = GROUP_ORDER.flatMap((g) => withIdx.filter((m) => m.group === g));
    const rest = withIdx.filter((m) => !GROUP_ORDER.includes(m.group));
    return [...known, ...rest];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const groupSpans = useMemo(() => {
    const out: { group: string; span: number }[] = [];
    for (const m of orderedMetrics) {
      const last = out[out.length - 1];
      if (last && last.group === m.group) last.span++;
      else out.push({ group: m.group, span: 1 });
    }
    return out;
  }, [orderedMetrics]);
  // Measure the group-header row so the metric-header row can stick right below it.
  const grpRowRef = useRef<HTMLTableRowElement>(null);
  const [grpH, setGrpH] = useState(22);
  useLayoutEffect(() => {
    if (grpRowRef.current) setGrpH(grpRowRef.current.getBoundingClientRect().height);
  }, [orderedMetrics.length, key, data]);
  const mmv = (idx: number) => {
    const xs = tableRows.map((r) => r.vals?.[idx]).filter((x): x is number => x != null);
    return xs.length ? [Math.min(...xs), Math.max(...xs)] : null;
  };

  const onsetHms = data ? fmtSec(data.onset) : "";
  const fnum = (x: number | null | undefined, dec = 1) =>
    x == null ? "—" : dec === 0 ? String(Math.round(x)) : x.toFixed(dec);
  // Generic value formatter for the mixed-scale metric grid.
  const fmtVal = (x: number | null | undefined) => {
    if (x == null) return "—";
    const a = Math.abs(x);
    if (a >= 1000) return Math.round(x).toLocaleString("ru-RU");
    if (a >= 100) return String(Math.round(x));
    if (a >= 10) return x.toFixed(1);
    return x.toFixed(2);
  };
  const grpLabel = (g: string) => t("incidents.grp_" + g);
  // Flagged events: differential protection (87H) + H01 breaker Q1.
  const diffEvents = useMemo(() => (data?.alarms ?? []).filter((a) => a.diff), [data]);
  const q1Events = useMemo(() => (data?.alarms ?? []).filter((a) => a.q1), [data]);
  const diffTimes = useMemo(() => new Set(diffEvents.map((e) => fmtSec(e.ts))), [diffEvents]);
  const q1Times = useMemo(() => new Set(q1Events.map((e) => fmtSec(e.ts))), [q1Events]);
  // Pre-fault deviations strictly BEFORE the onset → the "what led to it" banner.
  const metricLabel = useMemo(() => {
    const m = new Map((data?.metrics ?? []).map((x) => [x.key, x.label]));
    return (k: string) => m.get(k) ?? k;
  }, [data]);
  const preBefore = useMemo(() => (data?.points ?? [])
    .filter((p) => {
      if (!(p.pre?.length ?? 0)) return false;
      const sec = fmtSec(p.t);
      if (sec === fmtSec(data!.trip) || diffTimes.has(sec) || q1Times.has(sec)) return false;
      const overv = Math.max(p.rs ?? 0, p.st ?? 0, p.rt ?? 0) > (data?.band[1] ?? 21);
      // strictly before the onset, or an amber (non-overvoltage) anomaly second
      return p.t < (data?.onset ?? "") || !overv;
    })
    .map((p) => ({ sec: fmtSec(p.t), keys: p.pre! }))
    .slice(0, 8), [data, diffTimes, q1Times]);

  if (error) return <div className="alert error">{error}</div>;

  return (
    <div>
      {/* Event selector */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 8 }}>{t("incidents.pick")}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(list ?? []).map((it) => (
            <button key={it.key}
              onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setKey(it.key); }}
              style={{
                flex: 1, minWidth: 150, textAlign: "left", cursor: "pointer",
                padding: "10px 12px", borderRadius: 12,
                border: "1px solid " + (key === it.key ? "#ff453a" : "rgba(255,255,255,0.10)"),
                background: key === it.key ? "rgba(255,69,58,0.12)" : "var(--bg2)",
                color: "var(--text)",
              }}>
              <div style={{ fontWeight: 800, fontSize: 15, fontVariantNumeric: "tabular-nums" }}>
                {it.date} · {it.time}
              </div>
              <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 2 }}>{it.unit_label}</div>
            </button>
          ))}
        </div>
        {data && (
          <div style={{ fontSize: 11.5, color: "var(--hint)", marginTop: 10 }}>
            {t("incidents.trip")}: <b style={{ color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{fmtSec(data.trip)}</b>
            {" · "}{t("incidents.onset")}: <b style={{ color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{fmtSec(data.onset)}</b>
            {" · "}{t("incidents.window")}: <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtSec(data.tele_from)}–{fmtSec(data.tele_to)}</span>
          </div>
        )}
      </div>

      {!data && <div className="empty-state"><span className="spinner" /></div>}

      {data && (
        <>
          {/* Factual KPI */}
          <div className="auto-tiles" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
            <div className="auto-tile">
              <div className="auto-tile-top"><span className="auto-tile-icon">🔺</span>
                <span className="auto-tile-title">{t("incidents.kpi_umax")}</span></div>
              <div className="auto-tile-bigrow">
                <span className="auto-tile-big">{fnum(data.kpi.u_max)}</span>
                <span className="auto-tile-unit">кВ</span>
              </div>
            </div>
            <div className="auto-tile">
              <div className="auto-tile-top"><span className="auto-tile-icon">⚡</span>
                <span className="auto-tile-title">{t("incidents.kpi_kwmax")}</span></div>
              <div className="auto-tile-bigrow">
                <span className="auto-tile-big">{fnum(data.kpi.kw_max, 0)}</span>
                <span className="auto-tile-unit">кВт</span>
              </div>
            </div>
            <div className="auto-tile">
              <div className="auto-tile-top"><span className="auto-tile-icon">🔄</span>
                <span className="auto-tile-title">{t("incidents.kpi_qmax")}</span></div>
              <div className="auto-tile-bigrow">
                <span className="auto-tile-big">{fnum(data.kpi.q_max, 0)}</span>
                <span className="auto-tile-unit">кВАр</span>
              </div>
            </div>
            <div className="auto-tile">
              <div className="auto-tile-top"><span className="auto-tile-icon">〰️</span>
                <span className="auto-tile-title">{t("incidents.kpi_hz")}</span></div>
              <div className="auto-tile-bigrow">
                <span className="auto-tile-big" style={{ fontSize: 20 }}>
                  {fnum(data.kpi.hz_min)}–{fnum(data.kpi.hz_max)}
                </span>
                <span className="auto-tile-unit">Гц</span>
              </div>
            </div>
            <div className="auto-tile">
              <div className="auto-tile-top"><span className="auto-tile-icon">📈</span>
                <span className="auto-tile-title">{t("incidents.kpi_oob")}</span></div>
              <div className="auto-tile-bigrow">
                <span className="auto-tile-big">{data.kpi.oob_sec}</span>
                <span className="auto-tile-unit">{t("incidents.sec")}</span>
              </div>
            </div>
            <div className="auto-tile">
              <div className="auto-tile-top"><span className="auto-tile-icon">🚨</span>
                <span className="auto-tile-title">{t("incidents.kpi_alarms")}</span></div>
              <div className="auto-tile-bigrow">
                <span className="auto-tile-big">{data.kpi.alarms_n}</span>
                <span className="auto-tile-unit">{t("alarms.records")}</span>
              </div>
            </div>
            {data.kpi.speed_max != null && data.kpi.speed_max > 0 && (
              <div className="auto-tile"
                style={data.kpi.speed_max > 110 ? { border: "1.6px solid #ff9500" } : undefined}>
                <div className="auto-tile-top"><span className="auto-tile-icon">🌀</span>
                  <span className="auto-tile-title">{t("incidents.kpi_speed")}</span></div>
                <div className="auto-tile-bigrow">
                  <span className="auto-tile-big"
                    style={data.kpi.speed_max > 110 ? { color: "#ff9500" } : undefined}>
                    {fnum(data.kpi.speed_max, 0)}
                  </span>
                  <span className="auto-tile-unit">%</span>
                </div>
              </div>
            )}
          </div>

          {/* Highlighted key items: differential (87H) + H01 breaker Q1 */}
          {(diffEvents.length > 0 || q1Events.length > 0) && (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {diffEvents.length > 0 && (
                <div style={{ padding: "9px 12px", borderRadius: 10, background: "rgba(191,90,242,0.14)", borderLeft: `4px solid ${DIFF_COLOR}`, fontSize: 12.5 }}>
                  <b style={{ color: DIFF_COLOR }}>⬥ {t("incidents.diff_label")}</b>
                  {" — "}{t("incidents.fired")}: <b style={{ fontVariantNumeric: "tabular-nums" }}>{diffEvents.map((e) => fmtSec(e.ts)).join(", ")}</b>
                </div>
              )}
              {q1Events.length > 0 && (
                <div style={{ padding: "9px 12px", borderRadius: 10, background: "rgba(50,173,230,0.14)", borderLeft: `4px solid ${Q1_COLOR}`, fontSize: 12.5 }}>
                  <b style={{ color: Q1_COLOR }}>▮ {t("incidents.q1_label")}</b>
                  {" — "}{t("incidents.fired")}: <b style={{ fontVariantNumeric: "tabular-nums" }}>{q1Events.map((e) => fmtSec(e.ts)).join(", ")}</b>
                </div>
              )}
            </div>
          )}

          {/* Pre-fault deviations (before the onset) */}
          {preBefore.length > 0 && (
            <div style={{ marginTop: 8, padding: "9px 12px", borderRadius: 10, background: "rgba(255,214,10,0.12)", borderLeft: `4px solid ${PRE_COLOR}`, fontSize: 12.5 }}>
              <b style={{ color: "#d9b100" }}>⚠ {t("incidents.pre_label")}</b>
              <div style={{ marginTop: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                {preBefore.map((p) => (
                  <div key={p.sec} style={{ fontSize: 12 }}>
                    <b style={{ fontVariantNumeric: "tabular-nums" }}>{p.sec}</b>
                    {" — "}{p.keys.map(metricLabel).join(", ")}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Voltage chart */}
          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("incidents.chart_v")}</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={rows} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={45} />
                <YAxis tick={{ fontSize: 10 }} width={40} domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{ background: "var(--bg2)", border: "1px solid var(--border)", fontSize: 12 }}
                  labelFormatter={(_l, p) => (p && p[0] ? (p[0].payload as { full: string }).full : "")}
                  formatter={(v: unknown) => (typeof v === "number" ? v.toFixed(2) : String(v ?? ""))}
                />
                <ReferenceArea y1={data.band[0]} y2={data.band[1]} fill="#34c759" fillOpacity={0.08}
                  ifOverflow="extendDomain" />
                <ReferenceLine y={data.band[0]} stroke="#34c759" strokeDasharray="4 3" ifOverflow="extendDomain" />
                <ReferenceLine y={data.band[1]} stroke="#34c759" strokeDasharray="4 3" ifOverflow="extendDomain" />
                <ReferenceLine x={onsetHms} stroke="#8e8e93" strokeDasharray="3 3" />
                <ReferenceLine x={tripHms} stroke="#ff453a" strokeWidth={1.5}
                  label={{ value: "◆", position: "top", fill: "#ff453a", fontSize: 12 }} />
                {diffEvents.map((e, i) => (
                  <ReferenceLine key={"d" + i} x={fmtSec(e.ts)} stroke={DIFF_COLOR} strokeWidth={1.6} strokeDasharray="2 2"
                    label={{ value: "87H", position: "insideTopLeft", fill: DIFF_COLOR, fontSize: 9 }} />
                ))}
                {q1Events.map((e, i) => (
                  <ReferenceLine key={"q" + i} x={fmtSec(e.ts)} stroke={Q1_COLOR} strokeWidth={1.6} strokeDasharray="2 2"
                    label={{ value: "Q1", position: "insideTopRight", fill: Q1_COLOR, fontSize: 9 }} />
                ))}
                <Line type="monotone" dataKey="rs" name="RS" stroke="#2481cc" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="st" name="ST" stroke="#e3350d" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="rt" name="RT" stroke="#ff9500" strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: "var(--hint)", textAlign: "center" }}>
              <span style={{ color: "#2481cc" }}>RS</span> · <span style={{ color: "#e3350d" }}>ST</span> · <span style={{ color: "#ff9500" }}>RT</span>, кВ
              {" · "}<span style={{ color: "#ff453a" }}>◆ {t("incidents.marker_trip")}</span>
              {" · "}<span>┆ {t("incidents.marker_onset")}</span>
            </div>
          </div>

          {/* Power / frequency chart */}
          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("incidents.chart_p")}</div>
            <ResponsiveContainer width="100%" height={210}>
              <LineChart data={rows} margin={{ top: 4, right: 6, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={45} />
                <YAxis yAxisId="p" tick={{ fontSize: 9 }} width={44} domain={["auto", "auto"]} />
                <YAxis yAxisId="hz" orientation="right" tick={{ fontSize: 9, fill: "#34c759" }} width={32} domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{ background: "var(--bg2)", border: "1px solid var(--border)", fontSize: 12 }}
                  labelFormatter={(_l, p) => (p && p[0] ? (p[0].payload as { full: string }).full : "")} />
                <ReferenceLine yAxisId="p" x={tripHms} stroke="#ff453a" strokeWidth={1.5} />
                {diffEvents.map((e, i) => <ReferenceLine key={"pd" + i} yAxisId="p" x={fmtSec(e.ts)} stroke={DIFF_COLOR} strokeWidth={1.4} strokeDasharray="2 2" />)}
                {q1Events.map((e, i) => <ReferenceLine key={"pq" + i} yAxisId="p" x={fmtSec(e.ts)} stroke={Q1_COLOR} strokeWidth={1.4} strokeDasharray="2 2" />)}
                <Line yAxisId="p" type="monotone" dataKey="kw" name="P, кВт" stroke="#2481cc" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line yAxisId="p" type="monotone" dataKey="kvar" name="Q, кВАр" stroke="#af52de" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line yAxisId="hz" type="monotone" dataKey="hz" name="f, Гц" stroke="#34c759" strokeWidth={1.3} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: "var(--hint)", textAlign: "center" }}>
              <span style={{ color: "#2481cc" }}>P, кВт</span> · <span style={{ color: "#af52de" }}>Q, кВАр</span> · <span style={{ color: "#34c759" }}>f, Гц</span>
            </div>
          </div>

          {/* Guide vane + turbine speed chart */}
          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("incidents.chart_pos")}</div>
            <ResponsiveContainer width="100%" height={190}>
              <LineChart data={rows} margin={{ top: 4, right: 6, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={45} />
                <YAxis tick={{ fontSize: 9 }} width={36} domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{ background: "var(--bg2)", border: "1px solid var(--border)", fontSize: 12 }}
                  labelFormatter={(_l, p) => (p && p[0] ? (p[0].payload as { full: string }).full : "")} />
                <ReferenceLine y={100} stroke="#8e8e93" strokeDasharray="3 3" />
                <ReferenceLine x={tripHms} stroke="#ff453a" strokeWidth={1.5} />
                <Line type="monotone" dataKey="speed" name="n, %" stroke="#e3350d" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="pos" name="НА, %" stroke="#2481cc" strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: "var(--hint)", textAlign: "center" }}>
              <span style={{ color: "#e3350d" }}>{t("incidents.speed_label")}</span> · <span style={{ color: "#2481cc" }}>{t("incidents.pos_label")}</span>
            </div>
          </div>

          {/* Alarm log */}
          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{t("incidents.alarms_title")}</div>
            <div style={{ overflow: "auto", maxHeight: "56vh" }}>
              <table className="table" style={{ fontSize: 11.5, borderCollapse: "separate", borderSpacing: 0 }}>
                <thead><tr>
                  {[t("combined.col_time"), t("alarms.col_unit"), "Alarm (EN)", t("incidents.col_ru")].map((hh, i) => (
                    <th key={i} style={{ position: "sticky", top: 0, zIndex: 2, background: "var(--bg2)", boxShadow: "0 1px 0 rgba(127,127,127,0.3)" }}>{hh}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {data.alarms.map((a, i) => {
                    const hl = a.diff ? DIFF_COLOR : a.q1 ? Q1_COLOR : null;
                    return (
                    <tr key={i} style={
                      hl ? { background: hl + "26", fontWeight: 600 }
                        : a.category === "TRIP" ? { background: "rgba(255,69,58,0.08)" } : undefined}>
                      <td style={{
                        whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
                        borderLeft: hl ? `3px solid ${hl}` : undefined,
                      }}>{fmtSec(a.ts)}</td>
                      <td>
                        <span style={{
                          padding: "1px 6px", background: UNIT_COLOR[a.unit] || "var(--hint)",
                          color: "white", borderRadius: 5, fontWeight: 700, fontSize: 10, whiteSpace: "nowrap",
                        }}>{a.unit}</span>
                      </td>
                      <td>
                        {hl && (
                          <span style={{
                            padding: "1px 6px", background: hl, color: "white", borderRadius: 5,
                            fontWeight: 800, fontSize: 9.5, marginRight: 6, whiteSpace: "nowrap",
                          }}>{a.diff ? "87H" : "Q1"}</span>
                        )}
                        <span style={{ color: CAT_COLOR[a.category] || "var(--hint)", fontWeight: 700, fontSize: 10, marginRight: 6 }}>
                          {a.category}
                        </span>
                        {a.en}
                      </td>
                      <td>{a.ru}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-second table — ALL SCADA metrics */}
          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              {t("incidents.table_title")}
              <span style={{ fontWeight: 400, color: "var(--hint)", fontSize: 12 }}>
                {" · "}{orderedMetrics.length} {t("incidents.metrics_n")}
              </span>
            </div>
            <div style={{ overflow: "auto", maxHeight: "72vh" }}>
              <table className="table" style={{ fontSize: 10.5, borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr ref={grpRowRef}>
                    <th rowSpan={2} style={{
                      position: "sticky", left: 0, top: 0, zIndex: 6, background: "var(--bg2)",
                      boxShadow: "1px 0 0 rgba(127,127,127,0.3)", verticalAlign: "bottom",
                    }}>{t("combined.col_time")}</th>
                    {groupSpans.map((g, i) => (
                      <th key={i} colSpan={g.span} style={{
                        position: "sticky", top: 0, zIndex: 4, textAlign: "center",
                        background: "var(--bg2)", fontSize: 10,
                      }}>{grpLabel(g.group)}</th>
                    ))}
                  </tr>
                  <tr>
                    {orderedMetrics.map((m) => (
                      <th key={m.key} style={{
                        position: "sticky", top: grpH, zIndex: 4, background: "var(--bg2)",
                        minWidth: 44, whiteSpace: "nowrap", verticalAlign: "bottom", padding: "2px 5px",
                        boxShadow: "0 1px 0 rgba(127,127,127,0.3)",
                      }}>
                        <div style={{ fontSize: 9.5, lineHeight: 1.1, fontWeight: 600 }}>{m.label}</div>
                        <div style={{ fontSize: 8.5, color: "var(--hint)", fontWeight: 400 }}>{m.unit || "—"}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((r) => {
                    const isTrip = r.label === tripHms;
                    // Q1 / differential seconds get their own colour, over the trip/OOB tint.
                    const evHl = diffTimes.has(r.label) ? DIFF_COLOR : q1Times.has(r.label) ? Q1_COLOR : null;
                    const overv = Math.max(r.rs ?? 0, r.st ?? 0, r.rt ?? 0) > data.band[1];
                    const isPre = !evHl && !isTrip && !overv && ((r as { pre?: string[] }).pre?.length ?? 0) > 0;
                    const rowBg = evHl ? evHl + "2e" : isTrip ? "rgba(255,69,58,0.20)"
                      : overv ? "rgba(255,69,58,0.06)" : isPre ? "rgba(255,214,10,0.14)" : undefined;
                    const timeBg = evHl ? (evHl === DIFF_COLOR ? "#3a2a4a" : "#183a44")
                      : isTrip ? "#5a2a2e" : isPre ? "#4a3f10" : "var(--bg2)";
                    const marker = evHl ? (evHl === DIFF_COLOR ? "⬥ " : "▮ ") : isTrip ? "◆ " : isPre ? "⚠ " : "";
                    return (
                      <tr key={r.label} style={(isTrip || evHl || isPre) ? { fontWeight: 700 } : undefined}>
                        <td style={{
                          position: "sticky", left: 0, zIndex: 1,
                          background: timeBg,
                          borderLeft: evHl ? `3px solid ${evHl}` : isPre ? `3px solid ${PRE_COLOR}` : undefined,
                          boxShadow: "1px 0 0 rgba(127,127,127,0.3)",
                          whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
                        }}>{marker}{r.label}</td>
                        {orderedMetrics.map((m) => {
                          const v = r.vals?.[m.idx];
                          const isV = m.key === "voltage_rs" || m.key === "voltage_st" || m.key === "voltage_rt";
                          const over = isV && (v ?? 0) > data.band[1];
                          const overspd = m.key === "turbine_speed" && (v ?? 0) > 110;
                          const dev = isPre && (r.pre?.includes(m.key) ?? false);
                          return (
                            <td key={m.key} className="num" style={{
                              background: rowBg,
                              color: over ? "#ff453a" : overspd ? "#ff9500" : dev ? "#d9b100" : undefined,
                              fontWeight: (overspd || dev) ? 700 : undefined,
                            }}>{fmtVal(v)}</td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {(["min", "max"] as const).map((which, wi) => (
                    <tr key={which}>
                      <td style={{
                        position: "sticky", left: 0, zIndex: 1, background: "var(--bg2)",
                        boxShadow: "1px 0 0 rgba(127,127,127,0.3)", fontWeight: 700, color: "var(--hint)",
                        borderTop: wi === 0 ? "2px solid rgba(127,127,127,0.4)" : undefined,
                      }}>{t("minmax." + which)}</td>
                      {orderedMetrics.map((m) => {
                        const r = mmv(m.idx);
                        return (
                          <td key={m.key} className="num" style={{
                            fontWeight: 700, background: "var(--bg2)",
                            borderTop: wi === 0 ? "2px solid rgba(127,127,127,0.4)" : undefined,
                          }}>{r ? fmtVal(r[wi]) : "—"}</td>
                        );
                      })}
                    </tr>
                  ))}
                </tfoot>
              </table>
            </div>
            <div style={{ fontSize: 10.5, color: "var(--hint)", marginTop: 6 }}>{t("incidents.table_note")}</div>
          </div>
        </>
      )}
    </div>
  );
}
